import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Camera } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  'D&D', 'Chess', 'Catan', 'Magic: The Gathering',
  'Pokemon', 'Yu-Gi-Oh!', 'Warhammer', 'Monopoly',
  'Ticket to Ride', 'Scrabble', 'Risk', 'Other'
];

const PLAY_STYLES = [
  { value: 'competitive', label: 'Competitive', desc: 'I play to win!' },
  { value: 'casual', label: 'Casual', desc: 'Just having fun' },
  { value: 'teaching', label: 'Teacher', desc: 'Love teaching new players' },
  { value: 'learning', label: 'Learning', desc: 'Still learning the ropes' },
  { value: 'social', label: 'Social', desc: 'Here for the company' }
];

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    location: '',
    avatar: '',
    preferred_games: [],
    play_style: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        bio: user.bio || '',
        location: user.location || '',
        avatar: user.avatar || '',
        preferred_games: user.preferred_games || [],
        play_style: user.play_style || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'username') {
      setFormData(prev => ({ ...prev, [name]: value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleGame = (game) => {
    setFormData(prev => ({
      ...prev,
      preferred_games: prev.preferred_games.includes(game)
        ? prev.preferred_games.filter(g => g !== game)
        : [...prev.preferred_games, game]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.put(`${API_URL}/api/users/profile`, formData, { withCredentials: true });
      updateUser(data);
      // Notify the onboarding checklist to refetch — user may have completed
      // complete_profile / pick_favorite_games / set_location.
      try {
        const { triggerChecklistRefresh } = await import('./OnboardingWizard');
        triggerChecklistRefresh();
      } catch { /* ignore */ }
      toast.success('Profile updated!');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="app-container" data-testid="edit-profile-page">
      {/* Header */}
      <header className="p-6 pb-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Edit Profile</h1>
          <p className="text-[#71717A] text-sm">Update your info</p>
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-6 pb-32 space-y-5" data-testid="edit-profile-form">
        {/* Avatar Preview + Upload */}
        <div className="flex justify-center">
          <div className="relative">
            {formData.avatar ? (
              <img src={formData.avatar.startsWith('data:') ? formData.avatar : formData.avatar} alt="Avatar" className="w-24 h-24 rounded-full object-cover border-4 border-[#9D4CDD]/30" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-2xl font-bold text-white border-4 border-[#9D4CDD]/30">
                {getInitials(formData.name)}
              </div>
            )}
            <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#9D4CDD] flex items-center justify-center cursor-pointer hover:bg-[#7C3AED] transition-colors" data-testid="avatar-upload-btn">
              <Camera size={16} weight="fill" className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
                const reader = new FileReader();
                reader.onload = async (ev) => {
                  const b64 = ev.target.result;
                  setFormData(prev => ({ ...prev, avatar: b64 }));
                  try {
                    await axios.post(`${API_URL}/api/users/avatar`, { image_data: b64 }, { withCredentials: true });
                    toast.success('Avatar uploaded!');
                  } catch (err) { toast.error('Upload failed'); }
                };
                reader.readAsDataURL(file);
              }} />
            </label>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Display Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} className="input-field" placeholder="Your name" required data-testid="name-input" />
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Username</label>
          <input type="text" name="username" value={formData.username} onChange={handleChange} className="input-field" placeholder="unique_username" data-testid="username-input" />
          <p className="text-[#71717A] text-xs mt-1">Lowercase, numbers, _ and - only</p>
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Bio</label>
          <textarea name="bio" value={formData.bio} onChange={handleChange} className="input-field min-h-[80px] resize-none" placeholder="Tell others about yourself..." data-testid="bio-input" />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Location</label>
          <input type="text" name="location" value={formData.location} onChange={handleChange} className="input-field" placeholder="City, State" data-testid="location-input" />
        </div>

        {/* Avatar URL */}
        {/* Play Style */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-3">Play Style</label>
          <div className="grid grid-cols-2 gap-2">
            {PLAY_STYLES.map((style) => (
              <button
                key={style.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, play_style: style.value }))}
                className={`p-3 rounded-xl text-left transition-all ${
                  formData.play_style === style.value
                    ? 'bg-[#9D4CDD]/20 border border-[#9D4CDD]/50'
                    : 'bg-[#181824] border border-transparent hover:border-white/10'
                }`}
                data-testid={`play-style-${style.value}`}
              >
                <p className="text-white text-sm font-medium">{style.label}</p>
                <p className="text-[#71717A] text-xs">{style.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Preferred Games */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-3">Favorite Games</label>
          <div className="flex flex-wrap gap-2">
            {GAME_TYPES.map((game) => (
              <GamePill key={game} game={game} selected={formData.preferred_games.includes(game)} onClick={() => toggleGame(game)} />
            ))}
          </div>
        </div>

        {/* Submit */}
        <motion.button type="submit" disabled={loading} className="btn-primary w-full mt-6 flex items-center justify-center gap-2" whileTap={{ scale: 0.98 }} data-testid="save-profile-button">
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </span>
          ) : (
            <>
              <Check size={20} weight="bold" />
              Save Changes
            </>
          )}
        </motion.button>
      </form>
    </div>
  );
}
